import Phaser from 'phaser';
import { useStore } from '@/infra/store';
import { eventBus } from '@/infra/event-bus/EventBus';
import { logger } from '@/infra/logger/Logger';
import {
  PLAYER_SPEED,
  WORLD_PIXEL_W,
  WORLD_PIXEL_H,
} from '@/domain/world/types';
import {
  ATTACK_HITBOX_W,
  ATTACK_HITBOX_H,
  DODGE_DURATION_MS,
} from '@/domain/expedition/types';
import { regionRegistry } from '@/domain/world/region-registry';
import { getInputAdapter } from '@/services/input';
import { getCombatRunService } from '@/services/combat-run';
import { buildWorldRenderer, type WorldRendererHandle } from '../render/WorldRenderer';
import {
  buildDirectionalCreatureRenderer,
  type DirectionalCreatureRendererHandle,
} from '../render/DirectionalCreatureRenderer';
import { buildEnemyRenderer, type EnemyRendererHandle } from '../render/EnemyRenderer';
import { buildEffects, type EffectsApi } from '../render/EffectsRenderer';
import { buildCombatTelegraph, type CombatTelegraphHandle } from '../render/CombatTelegraphRenderer';
import { buildProjectileRenderer, type ProjectileRendererHandle } from '../render/ProjectileRenderer';
import { moveActorInActiveRegion } from './region-movement-adapter';
import type { ActorFootprint } from '@/domain/world/geometry';
import { facingFromVector, footprintForPresentation } from '@/domain/world/actor-presentation';
import { displayHeightForStage } from '@/domain/world/world-scale';
import { cameraViewForRegion, worldYSortDepth } from './world-visual-adapter';

const DODGE_DISTANCE = 96;

export class ExpeditionScene extends Phaser.Scene {
  static readonly KEY = 'ExpeditionScene';

  private worldRenderer?: WorldRendererHandle;
  private creatureRenderer?: DirectionalCreatureRendererHandle;
  private enemyRenderers = new Map<number, EnemyRendererHandle>();
  private telegraphRenderers = new Map<number, CombatTelegraphHandle>();
  private projectileRenderers = new Map<string, ProjectileRendererHandle>();
  private effects?: EffectsApi;
  private vignette?: Phaser.GameObjects.Rectangle;
  private storeUnsub?: () => void;
  private busUnsubs: Array<() => void> = [];
  private playerRadius = 18;
  private playerFootprint: ActorFootprint = { radiusX: 18, radiusY: 18 };
  private alive = true;
  private lastFacingX = 1;
  private lastFacingY = 0;
  private reducedMotion = false;

  constructor() {
    super({ key: ExpeditionScene.KEY });
  }

  create(): void {
    this.alive = true;
    const c = useStore.getState().creature;
    if (!c) {
      logger.warn('expedition-scene.no-creature');
      return;
    }

    const reducedMotion = useStore.getState().settings.accessibility.reducedMotion;
    this.reducedMotion = reducedMotion;
    this.worldRenderer = buildWorldRenderer(this);
    this.creatureRenderer = buildDirectionalCreatureRenderer(this, c, { reducedMotion });
    this.playerFootprint = footprintForPresentation(c.genome.body, displayHeightForStage(c.lifecycle.stage));
    const start = useStore.getState();
    this.creatureRenderer.container.setPosition(start.playerX, start.playerY);
    this.effects = buildEffects(this, { reducedMotion });

    // Spawn enemies já criados pelo combat-run.start
    const enemies = useStore.getState().enemies;
    for (const e of enemies) {
      const r = buildEnemyRenderer(this, e, reducedMotion);
      this.enemyRenderers.set(e.rid, r);
      this.telegraphRenderers.set(e.rid, buildCombatTelegraph(this, e, reducedMotion));
    }

    // Câmera
    const runBiome = start.activeRun?.biome ?? regionRegistry.regionAtWorldPoint({
      x: start.playerX,
      y: start.playerY,
    }).biome;
    const cameraView = cameraViewForRegion(regionRegistry, runBiome);
    this.cameras.main.setBounds(
      cameraView.bounds.x,
      cameraView.bounds.y,
      cameraView.bounds.w,
      cameraView.bounds.h,
    );
    this.cameras.main.startFollow(this.creatureRenderer.container, true, 0.1, 0.1);
    this.cameras.main.setZoom(cameraView.zoom);

    // Vinheta vermelha (sensação de perigo)
    this.vignette = this.add
      .rectangle(0, 0, WORLD_PIXEL_W, WORLD_PIXEL_H, 0x6a0a0a, 0.18)
      .setOrigin(0)
      .setDepth(40)
      .setScrollFactor(1);

    // Subscribers
    this.storeUnsub = useStore.subscribe((s) => {
      if (!this.alive) return;
      // Update creature renderer
      if (s.creature && this.creatureRenderer) {
        this.creatureRenderer.updateCreature(s.creature);
        this.playerFootprint = footprintForPresentation(
          s.creature.genome.body,
          displayHeightForStage(s.creature.lifecycle.stage),
        );
      }
      // Sync enemies
      this.syncEnemies();
      this.syncProjectiles();
      if (!s.inExpedition) {
        this.alive = false;
        this.scene.stop(ExpeditionScene.KEY);
        // Próxima cena depende: se inWorld true, volta ao mundo. Senão, casa.
        if (s.inWorld) this.scene.start('WorldScene');
        else this.scene.start('HouseScene');
      }
    });

    this.busUnsubs.push(
      eventBus.on('combat.enemy-hit', (p) => {
        const r = this.enemyRenderers.get(p.rid);
        if (r) r.flashHit();
        const e = useStore.getState().enemies.find((x) => x.rid === p.rid);
        if (e && this.effects) this.effects.popDamage(e.x, e.y - 14, p.damage, p.crit);
      }),
    );
    this.busUnsubs.push(
      eventBus.on('combat.player-hit', () => {
        if (this.creatureRenderer) {
          this.creatureRenderer.playHurt(
            facingFromVector(this.lastFacingX, this.lastFacingY, 'right'),
          );
          this.tweens.add({ targets: this.creatureRenderer.container, alpha: 0.4, duration: 70, yoyo: true });
        }
        if (!this.reducedMotion) this.cameras.main.shake(120, 0.003);
      }),
    );
    this.busUnsubs.push(
      eventBus.on('combat.attack-used', ({ dirX, dirY, atMs }) => {
        this.creatureRenderer?.playAttack(facingFromVector(dirX, dirY, 'right'), atMs);
      }),
    );
    this.busUnsubs.push(
      eventBus.on('combat.dodge-used', ({ dirX, dirY, atMs }) => {
        this.creatureRenderer?.playDodge(facingFromVector(dirX, dirY, 'right'), atMs);
      }),
    );
    this.busUnsubs.push(
      eventBus.on('combat.skill-used', ({ type, dirX, dirY, atMs }) => {
        this.creatureRenderer?.playSkill(facingFromVector(dirX, dirY, 'right'), type, atMs);
        if (this.effects && this.creatureRenderer) {
          this.effects.skillBurst(
            this.creatureRenderer.container.x,
            this.creatureRenderer.container.y,
            type,
            useStore.getState().creature?.genome,
          );
        }
      }),
    );

    getInputAdapter().install();

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.alive = false;
      this.storeUnsub?.();
      this.busUnsubs.forEach((u) => u());
      this.busUnsubs = [];
      this.worldRenderer?.destroy();
      this.creatureRenderer?.destroy();
      this.enemyRenderers.forEach((r) => r.destroy());
      this.enemyRenderers.clear();
      this.telegraphRenderers.forEach((r) => r.destroy());
      this.telegraphRenderers.clear();
      this.projectileRenderers.forEach((r) => r.destroy());
      this.projectileRenderers.clear();
      this.vignette?.destroy();
    });
  }

  override update(_time: number, delta: number): void {
    if (!this.alive || !this.creatureRenderer) return;
    const input = getInputAdapter();
    const v = input.getMovementVector();
    const now = Date.now();

    if (v.x !== 0 || v.y !== 0) {
      this.lastFacingX = v.x;
      this.lastFacingY = v.y;
    }

    if (input.isDodgePressed()) {
      getCombatRunService().dodge(this.lastFacingX, this.lastFacingY, now);
    }
    const clock = useStore.getState().actionClock;
    const dodging = now < clock.dodgeUntil;
    const moveVector = dodging ? useStore.getState().dodgeVector : v;
    const speed = dodging ? DODGE_DISTANCE / (DODGE_DURATION_MS / 1_000) : PLAYER_SPEED;
    if (moveVector.x !== 0 || moveVector.y !== 0) {
      this.movePlayer(moveVector.x, moveVector.y, speed, delta / 1_000);
    } else {
      this.creatureRenderer.setMovement(0, 0, delta);
    }
    this.creatureRenderer.setWorldDepth(worldYSortDepth(this.creatureRenderer.container.y).object);

    useStore.getState().setPlayerPos(this.creatureRenderer.container.x, this.creatureRenderer.container.y);

    if (input.isAttackPressed()) {
      this.doAttack(now);
    }
    if (input.isSkillPressed()) {
      const accepted = getCombatRunService().useSkill(this.lastFacingX, this.lastFacingY, now);
      if (accepted) this.syncPlayerFromStoreWithCollision();
    }

    // Tick enemies
    getCombatRunService().tickEnemies(now);

    // Update enemy positions visually
    this.updateCombatVisuals(now);
  }

  private doAttack(nowMs: number): void {
    if (!this.creatureRenderer || !this.effects) return;
    const px = this.creatureRenderer.container.x;
    const py = this.creatureRenderer.container.y;
    const dx = this.lastFacingX || 1;
    const dy = this.lastFacingY;
    const mag = Math.hypot(dx, dy) || 1;
    const ux = dx / mag;
    const uy = dy / mag;
    const hx = px + ux * (ATTACK_HITBOX_W / 2 + 4);
    const hy = py + uy * (ATTACK_HITBOX_W / 2 + 4);
    if (getCombatRunService().attack(ux, uy, nowMs)) {
      this.effects.flashHitbox(hx, hy, ATTACK_HITBOX_W, ATTACK_HITBOX_H);
    }
  }

  private movePlayer(dx: number, dy: number, speed: number, dt: number): void {
    if (!this.creatureRenderer) return;
    const from = { x: this.creatureRenderer.container.x, y: this.creatureRenderer.container.y };
    const resolution = moveActorInActiveRegion({
      registry: regionRegistry,
      from,
      velocity: { x: dx, y: dy },
      speed,
      dtSec: dt,
      radius: this.playerRadius,
      footprint: this.playerFootprint,
      allowTransitions: false,
    });
    this.creatureRenderer.container.setPosition(resolution.position.x, resolution.position.y);
    this.creatureRenderer.setMovement(
      resolution.position.x - from.x,
      resolution.position.y - from.y,
      dt * 1_000,
    );
  }

  private syncPlayerFromStoreWithCollision(): void {
    if (!this.creatureRenderer) return;
    const state = useStore.getState();
    const from = { x: this.creatureRenderer.container.x, y: this.creatureRenderer.container.y };
    const resolution = moveActorInActiveRegion({
      registry: regionRegistry,
      from,
      velocity: { x: state.playerX - from.x, y: state.playerY - from.y },
      speed: 1,
      dtSec: 1,
      radius: this.playerRadius,
      footprint: this.playerFootprint,
      allowTransitions: false,
    });
    const region = regionRegistry.regionAtWorldPoint(resolution.position);
    const position = regionRegistry.nearestValidPoint(
      region.id,
      resolution.position,
      Math.max(this.playerFootprint.radiusX, this.playerFootprint.radiusY),
    );
    this.creatureRenderer.container.setPosition(position.x, position.y);
    state.setPlayerPos(position.x, position.y);
  }

  private syncEnemies(): void {
    const enemies = useStore.getState().enemies;
    const aliveIds = new Set<number>();
    for (const e of enemies) {
      aliveIds.add(e.rid);
      if (!this.enemyRenderers.has(e.rid)) {
        const r = buildEnemyRenderer(this, e, this.reducedMotion);
        this.enemyRenderers.set(e.rid, r);
        this.telegraphRenderers.set(e.rid, buildCombatTelegraph(this, e, this.reducedMotion));
      }
      if (e.state === 'dead') {
        this.enemyRenderers.get(e.rid)?.destroy();
        this.enemyRenderers.delete(e.rid);
        this.telegraphRenderers.get(e.rid)?.destroy();
        this.telegraphRenderers.delete(e.rid);
      }
    }
    // Remove órfãos
    for (const [rid, r] of this.enemyRenderers) {
      if (!aliveIds.has(rid)) {
        r.destroy();
        this.enemyRenderers.delete(rid);
        this.telegraphRenderers.get(rid)?.destroy();
        this.telegraphRenderers.delete(rid);
      }
    }
  }

  private syncProjectiles(): void {
    const projectiles = useStore.getState().projectiles;
    const aliveIds = new Set(projectiles.map((projectile) => projectile.id));
    for (const projectile of projectiles) {
      if (!this.projectileRenderers.has(projectile.id)) {
        this.projectileRenderers.set(projectile.id, buildProjectileRenderer(this, projectile));
      }
    }
    for (const [id, renderer] of this.projectileRenderers) {
      if (!aliveIds.has(id)) {
        renderer.destroy();
        this.projectileRenderers.delete(id);
      }
    }
  }

  private updateCombatVisuals(nowMs: number): void {
    const state = useStore.getState();
    const enemies = state.enemies;
    for (const e of enemies) {
      const r = this.enemyRenderers.get(e.rid);
      if (r) {
        r.setPos(e.x, e.y);
        r.setHp(e.hp, e.maxHp);
        r.setState(e);
      }
      this.telegraphRenderers.get(e.rid)?.update(e, state.playerX, state.playerY, nowMs);
    }
    this.syncProjectiles();
    for (const projectile of state.projectiles) {
      this.projectileRenderers.get(projectile.id)?.update(projectile);
    }
  }
}
